import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { safeBlobGet } from '../utils/fetchPdfBlob';
import { useAuth } from '../contexts/AuthContext';
import { AppShell } from '../components/AppShell';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft, CurrencyEur, Users, Receipt,
  EnvelopeSimple, CheckCircle, Clock,
  XCircle, UserCircle, Warning, X, Hourglass,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AdminMemberDetail } from '../components/admin/AdminMemberDetail';
import { AdminPlanEditor } from '../components/admin/AdminPlanEditor';
import { AdminMembersTab } from '../components/admin/AdminMembersTab';
import { AdminInvoicesTab } from '../components/admin/AdminInvoicesTab';
import { AdminPlansTab } from '../components/admin/AdminPlansTab';
import { AdminEmailsTab } from '../components/admin/AdminEmailsTab';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_MAP = {
  active: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  trialing: { label: 'Essai', color: 'bg-blue-100 text-blue-700', icon: Clock },
  trial_free: { label: 'Essai gratuit', color: 'bg-sky-100 text-sky-700', icon: Hourglass },
  grace_period: { label: 'Grace', color: 'bg-amber-100 text-amber-700', icon: Warning },
  cancelled: { label: 'Annule', color: 'bg-gray-200 text-gray-600', icon: X },
  expired: { label: 'Expire', color: 'bg-red-100 text-red-700', icon: XCircle },
  no_subscription: { label: 'Aucun', color: 'bg-gray-100 text-gray-500', icon: UserCircle },
};

const STAT_ITEMS = [
  { key: 'total_members', label: 'Membres', icon: Users, color: 'text-[#FF6B00]', bg: 'bg-orange-50' },
  { key: 'active', label: 'Actifs', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { key: 'trial', label: 'Essais', icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
  { key: 'expired', label: 'Expires', icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  { key: 'total_invoices', label: 'Factures', icon: Receipt, color: 'text-violet-600', bg: 'bg-violet-50' },
];

const TAB_ITEMS = [
  { id: 'members', label: 'Membres', icon: Users, countKey: 'total_members' },
  { id: 'invoices', label: 'Factures', icon: Receipt, countKey: 'total_invoices' },
  { id: 'plans', label: 'Plans', icon: CurrencyEur },
  { id: 'emails', label: 'Emails', icon: EnvelopeSimple },
];

export function AdminPlansPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [plans, setPlans] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editPlan, setEditPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('members');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [memberDetail, setMemberDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simPlanId, setSimPlanId] = useState('plan_trimestriel');

  const invoices = useMemo(() => dashboard?.invoices || [], [dashboard]);
  const members = useMemo(() => dashboard?.members || [], [dashboard]);
  const stats = useMemo(() => dashboard?.stats || {}, [dashboard]);
  const invoicesTotal = useMemo(() => invoices.reduce((s, i) => s + (i.amount || 0), 0).toFixed(2), [invoices]);

  const filteredMembers = useMemo(() => {
    return members.filter(m => {
      const matchSearch = !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || m.subscription_status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [members, search, statusFilter]);

  const loadData = useCallback(async () => {
    try {
      const [pRes, dRes, eRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans/all`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/admin/dashboard`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/admin/stripe-emails`, { withCredentials: true }).catch(() => null),
      ]);
      setPlans(pRes.data || []);
      setDashboard(dRes.data || null);
      if (eRes?.data) setEmailConfig(eRes.data);
    } catch (_err) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [setPlans, setDashboard, setEmailConfig, setLoading]);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard', { replace: true }); return; }
    loadData();
  }, [user, navigate, loadData]);

  const savePlan = async () => {
    if (!editPlan) return;
    setSaving(true);
    try {
      await axios.put(`${API}/subscriptions/plans/${editPlan.id}`, {
        name: editPlan.name, price: parseFloat(editPlan.price),
        description: editPlan.description, trial_days: parseInt(editPlan.trial_days) || 7,
        is_active: editPlan.is_active,
      }, { withCredentials: true });
      toast.success('Plan mis à jour');
      setEditPlan(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const downloadInvoice = async (invoiceId, invoiceNum) => {
    try {
      const res = await safeBlobGet(`${API}/subscriptions/invoices/${invoiceId}/pdf`);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `Facture_${invoiceNum}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (_err) { toast.error(_err.message || 'Erreur téléchargement'); }
  };

  const openMemberDetail = async (memberId) => {
    setLoadingDetail(true);
    setMemberDetail(null);
    try {
      const res = await axios.get(`${API}/subscriptions/admin/members/${memberId}`, { withCredentials: true });
      setMemberDetail(res.data);
    } catch (_err) { toast.error('Erreur chargement detail'); }
    finally { setLoadingDetail(false); }
  };

  const simulateStatus = async (memberId, status, planId) => {
    setSimulating(true);
    try {
      const res = await axios.post(`${API}/subscriptions/admin/members/${memberId}/simulate`,
        { status, plan_id: planId || 'plan_mensuel' }, { withCredentials: true });
      toast.success(res.data.message);
      await openMemberDetail(memberId);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur simulation'); }
    finally { setSimulating(false); }
  };

  if (loading) return (
    <AppShell>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
      </div>
    </AppShell>
  );

  return (
    <AppShell>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="admin-plans-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden" onClick={() => navigate(-1)}>
            <ArrowLeft size={22} />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827]" data-testid="admin-title">
              Gestion des abonnements
            </h1>
            <p className="text-sm text-[#4B5563]">{stats.total_members || 0} membre(s) inscrits</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats">
          {STAT_ITEMS.map(({ key, label, icon: Icon, color, bg }) => (
            <Card key={label} className="border">
              <CardContent className="p-3 sm:p-4 flex flex-col items-center text-center">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${bg} mb-1.5`}>
                  <Icon size={18} className={color} weight="fill" />
                </div>
                <p className="text-lg sm:text-xl font-black text-[#111827] leading-tight" data-testid={`stat-${label.toLowerCase()}`}>
                  {stats[key] || 0}
                </p>
                <p className="text-[10px] text-[#6B7280] font-medium mt-0.5">{label}</p>
              </CardContent>
            </Card>
          ))}
          <Card className="border">
            <CardContent className="p-3 sm:p-4 flex flex-col items-center text-center">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-50 mb-1.5">
                <CurrencyEur size={18} className="text-emerald-600" weight="fill" />
              </div>
              <p className="text-lg sm:text-xl font-black text-[#111827] leading-tight" data-testid="stat-revenu">
                {(stats.total_revenue || 0).toFixed(0)}<span className="text-xs font-medium text-[#9CA3AF] ml-1">EUR</span>
              </p>
              <p className="text-[10px] text-[#6B7280] font-medium mt-0.5">Revenu</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1" data-testid="admin-tabs">
          {TAB_ITEMS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold transition-all ${
                tab === t.id ? 'bg-white text-[#111827] shadow-sm' : 'text-[#6B7280] hover:text-[#374151]'
              }`} data-testid={`tab-${t.id}`}>
              <t.icon size={14} weight={tab === t.id ? 'fill' : 'regular'} />
              <span className="hidden sm:inline">{t.label}</span>
              {t.countKey && stats[t.countKey] !== undefined && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-[#FF6B00] text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {t.countKey === 'total_members' ? stats.total_members || 0 : stats[t.countKey] || 0}
                </span>
              )}
              {t.id === 'plans' && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-[#FF6B00] text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {plans.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === 'members' && (
          <AdminMembersTab filteredMembers={filteredMembers} search={search} setSearch={setSearch}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            openMemberDetail={openMemberDetail} STATUS_MAP={STATUS_MAP} />
        )}
        {tab === 'invoices' && (
          <AdminInvoicesTab invoices={invoices} invoicesTotal={invoicesTotal} downloadInvoice={downloadInvoice} />
        )}
        {tab === 'plans' && (
          <AdminPlansTab plans={plans} setEditPlan={setEditPlan} />
        )}
        {tab === 'emails' && (
          <AdminEmailsTab emailConfig={emailConfig} />
        )}
      </div>

      <AdminMemberDetail
        open={!!memberDetail || loadingDetail}
        onOpenChange={(o) => { if (!o) setMemberDetail(null); }}
        memberDetail={memberDetail} loadingDetail={loadingDetail}
        plans={plans} simPlanId={simPlanId} setSimPlanId={setSimPlanId}
        simulating={simulating} onSimulate={simulateStatus}
        downloadInvoice={downloadInvoice}
      />
      <AdminPlanEditor editPlan={editPlan} setEditPlan={setEditPlan} saving={saving} onSave={savePlan} />
    </AppShell>
  );
}
